import type { ReactNode } from 'react';
import { Box, Collapse, Drawer, Heading, Icon, IconButton, Link, List, Text } from '@ksyos/design-system';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import type { OrganizationType } from '@common/enums';
import type { IReduxState } from '@frontend/configureStore';
import { organizationTypesWithRightsToCreateAccountsAndEndEmployments } from '@common/enums';
import { EpisodeCategory } from '@common/enums/episodeCategory';
import { getSelectedLanguage } from '@frontend/translate';
import { useUiStateContext } from '@frontend/utils/hooks/useUiState';
import {
    episodeCategoryToRouteParam,
    supplyCategoryToRouteParam,
} from '../pages/orderSupplies/utils/orderSuppliesRouteUtils';
import { PopupButton } from './PopupButton';

interface ISupportCollapseProps {
    children: ReactNode;
    header: string;
}

function SupportCollapse({ header, children }: Readonly<ISupportCollapseProps>) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Box
            borderBottom="1px solid"
            borderBottomColor="neutral200"
        >
            <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                onClick={() => {
                    setIsOpen(!isOpen);
                }}
                py={4}
                sx={{ '&:hover': { backgroundColor: '#FAF9F8' }, cursor: 'pointer' }}
            >
                <Text size="sm">{header}</Text>
                <IconButton
                    icon={isOpen ? 'caret-up-big' : 'caret-down-big'}
                    iconColor={isOpen ? 'secondary' : 'subtle'}
                    size="small"
                />
            </Box>
            <Collapse isOpen={isOpen}>{children}</Collapse>
        </Box>
    );
}

const StyledTabs = styled(TabsPrimitive.Root)`
    display: flex;
    flex-direction: row;
    width: 100%;
`;

const StyledList = styled(TabsPrimitive.List)`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    width: 50%;
`;

const StyledTrigger = styled(TabsPrimitive.Trigger)`
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    margin: 0;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 14px;
    line-height: 20px;
    width: 100%;
    padding: 10px 16px;
    justify-content: space-between;
    box-shadow: none;
    color: ${(props) => props.theme.colors.neutral700};
    font-weight: 400;

    &:hover {
        background-color: ${(props) => props.theme.colors.neutral100};
        color: ${(props) => props.theme.colors.neutral700};
    }

    &:focus {
        outline: none;
        box-shadow: ${(props) => props.theme.colors.blue500} 0 0 0 2px inset;
    }

    &:focus:not(:focus-visible) {
        outline: none;
        box-shadow: none;
    }

    &[data-state='active'] {
        color: ${(props) => props.theme.colors.magenta700};
        box-shadow: inset 3px 0 0 ${(props) => props.theme.colors.magenta700};
        font-weight: 500;

        &:hover {
            color: ${(props) => props.theme.colors.magenta700};
            box-shadow: inset 3px 0 0 ${(props) => props.theme.colors.magenta700};
            background-color: ${(props) => props.theme.colors.neutral100};
        }

        &:focus {
            box-shadow: ${(props) => props.theme.colors.blue500} 0 0 0 2px inset;
        }

        &:focus:not(:focus-visible) {
            box-shadow: inset 3px 0 0 ${(props) => props.theme.colors.magenta700};
        }
    }
`;

const StyledContent = styled(TabsPrimitive.Content)`
    width: 50%;
    background-color: white;
    outline: none;
    min-height: 160px;
    padding: 8px;
`;

const StyledLink = styled(Link)`
    display: block;
    width: 100%;
    font-size: 14px;
    line-height: 20px;
    padding-left: 0;
    padding-top: 4px;
    padding-bottom: 4px;
    color: ${(props) => props.theme.colors.magenta700};
    font-weight: 500;

    &:hover {
        color: ${(props) => props.theme.colors.magenta700};
    }

    &:focus {
        outline: none;
        color: ${(props) => props.theme.colors.magenta700};
    }

    &:focus:not(:focus-visible) {
        outline: none;
    }

    ${({ theme }) => theme.mediaQueries.small} {
        padding-left: 16px;
    }
`;

export const Support = () => {
    const [value, setValue] = useState('tab1');
    const { uiState, dispatchUiStateAction } = useUiStateContext();
    const {
        id: practitionerId,
        selectedEmploymentId: employmentId,
        organizationId,
        organizationType,
    } = useSelector((state: IReduxState) => state.authentication);
    const belongsToAOrganizationTypeWithRightsToCreateAccountsAndEndEmployments =
        organizationTypesWithRightsToCreateAccountsAndEndEmployments.includes(organizationType as OrganizationType);
    const translate = getSelectedLanguage();

    const toggle = () => {
        dispatchUiStateAction({ isModalOpen: false, type: 'setIsSupportModalOpen' });
        setValue('tab1');
    };

    return (
        <>
            <PopupButton
                className="automation-toggle-support"
                onClick={() => dispatchUiStateAction({ isModalOpen: true, type: 'setIsSupportModalOpen' })}
                style={{ marginTop: '100px' }}
            >
                {translate.support.support}
            </PopupButton>
            <Drawer
                isOpen={uiState.isSupportModalOpen}
                onClose={toggle}
                size="large"
            >
                <Drawer.Header>{translate.support.header}</Drawer.Header>
                <Drawer.CloseButton />
                <Drawer.Body>
                    <Box
                        mb={[2, 4, 6, 8, 10]}
                        mt={1}
                    >
                        <StyledTabs
                            onValueChange={setValue}
                            orientation="vertical"
                            value={value}
                        >
                            <StyledList>
                                <StyledTrigger value="tab1">
                                    <Box
                                        alignItems="center"
                                        as="span"
                                        display="flex"
                                    >
                                        <Icon
                                            color={value === 'tab1' ? 'secondary' : 'minimal'}
                                            icon="organization"
                                            sx={{ mr: 2 }}
                                        />
                                        Accounts beheren
                                    </Box>
                                    <Icon
                                        color={value === 'tab1' ? 'secondary' : 'subtle'}
                                        icon="caret-right"
                                    />
                                </StyledTrigger>
                                <StyledTrigger value="tab2">
                                    <Box
                                        alignItems="center"
                                        as="span"
                                        display="flex"
                                    >
                                        <Icon
                                            color={value === 'tab2' ? 'secondary' : 'minimal'}
                                            icon="devices"
                                            sx={{ mr: 2 }}
                                        />
                                        Materialen bestellen
                                    </Box>
                                    <Icon
                                        color={value === 'tab2' ? 'secondary' : 'subtle'}
                                        icon="caret-right"
                                    ></Icon>
                                </StyledTrigger>
                                <StyledTrigger value="tab3">
                                    <Box
                                        alignItems="center"
                                        as="span"
                                        display="flex"
                                    >
                                        <Icon
                                            color={value === 'tab3' ? 'secondary' : 'minimal'}
                                            icon="settings"
                                            sx={{ mr: 2 }}
                                        />
                                        Persoonlijke instellingen
                                    </Box>
                                    <Icon
                                        color={value === 'tab3' ? 'secondary' : 'subtle'}
                                        icon="caret-right"
                                    />
                                </StyledTrigger>
                            </StyledList>
                            <StyledContent
                                tabIndex={-1}
                                value="tab1"
                            >
                                <List>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/organizations/${organizationId}/view`}
                                            variant="link"
                                        >
                                            Accounts inzien
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        {belongsToAOrganizationTypeWithRightsToCreateAccountsAndEndEmployments ? (
                                            <StyledLink
                                                onClick={toggle}
                                                to={`/organizations/${organizationId}/view`}
                                                variant="link"
                                            >
                                                Account aanvragen
                                            </StyledLink>
                                        ) : (
                                            <StyledLink
                                                href="https://www.ksyos.nl/aanvragen-wijzigen-account/"
                                                openInNewTab
                                                variant="externalLink"
                                            >
                                                Account aanvragen
                                            </StyledLink>
                                        )}
                                    </List.Item>
                                    <List.Item>
                                        {belongsToAOrganizationTypeWithRightsToCreateAccountsAndEndEmployments ? (
                                            <StyledLink
                                                onClick={toggle}
                                                to={`/practitioners/${practitionerId}/profile`}
                                                variant="link"
                                            >
                                                Account wijzigen
                                            </StyledLink>
                                        ) : (
                                            <StyledLink
                                                href="https://www.ksyos.nl/aanvragen-wijzigen-account/"
                                                openInNewTab
                                                variant="externalLink"
                                            >
                                                Account wijzigen
                                            </StyledLink>
                                        )}
                                    </List.Item>
                                    <List.Item>
                                        {belongsToAOrganizationTypeWithRightsToCreateAccountsAndEndEmployments ? (
                                            <StyledLink
                                                onClick={toggle}
                                                to={`/employments/${employmentId}/personalSettings/serviceEmailNotifications`}
                                                variant="link"
                                            >
                                                Account deactiveren
                                            </StyledLink>
                                        ) : (
                                            <StyledLink
                                                href="https://www.ksyos.nl/deactiveren-ksyos-account/"
                                                openInNewTab
                                                variant="externalLink"
                                            >
                                                Account deactiveren
                                            </StyledLink>
                                        )}
                                    </List.Item>
                                </List>
                            </StyledContent>
                            <StyledContent
                                tabIndex={-1}
                                value="tab2"
                            >
                                <List>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/materialenBestellen/${episodeCategoryToRouteParam(EpisodeCategory.cardiology)}`}
                                            variant="link"
                                        >
                                            {translate.episodeCategoryEnum.cardiology}
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/materialenBestellen/${episodeCategoryToRouteParam(EpisodeCategory.ophthalmology)}/${supplyCategoryToRouteParam('eyeDropRecordCards')}`}
                                            variant="link"
                                        >
                                            {translate.episodeCategoryEnum.ophthalmology}
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/materialenBestellen/${episodeCategoryToRouteParam(EpisodeCategory.allergology)}/${supplyCategoryToRouteParam('respiratoryAllergy')}`}
                                            variant="link"
                                        >
                                            {translate.episodeCategoryEnum.allergology}
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/materialenBestellen/${episodeCategoryToRouteParam(EpisodeCategory.general)}/${supplyCategoryToRouteParam('hardwareToken')}`}
                                            variant="link"
                                        >
                                            {translate.twoFactorAuthenticationType.verificationDevice}
                                        </StyledLink>
                                    </List.Item>
                                </List>
                            </StyledContent>
                            <StyledContent
                                tabIndex={-1}
                                value="tab3"
                            >
                                <List>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/employments/${employmentId}/personalSettings`}
                                            variant="link"
                                        >
                                            Contactgegevens
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/employments/${employmentId}/personalSettings`}
                                            variant="link"
                                        >
                                            Zorgmail
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/employments/${employmentId}/personalSettings/preferenceList`}
                                            variant="link"
                                        >
                                            Voorkeurslijsten
                                        </StyledLink>
                                    </List.Item>
                                    <List.Item>
                                        <StyledLink
                                            onClick={toggle}
                                            to={`/employments/${employmentId}/personalSettings/serviceEmailNotifications`}
                                            variant="link"
                                        >
                                            E-mail notificaties
                                        </StyledLink>
                                    </List.Item>
                                </List>
                            </StyledContent>
                        </StyledTabs>
                    </Box>
                    <Box mb={[4, 6]}>
                        <Heading
                            mb={2}
                            size="small"
                        >
                            Top 3 veelgestelde vragen
                        </Heading>
                        <SupportCollapse header="Hoe kan ik een account laten aanmaken of deze wijzigen?">
                            <Text
                                color="subtle"
                                pb={4}
                                pt={2}
                                size="sm"
                            >
                                {renderAccountCreationOrChangeInfoByOrganizationType()}
                            </Text>
                        </SupportCollapse>
                        <SupportCollapse header="Hoe kan ik een GGZ begeleiding opnieuw versturen?">
                            <Text
                                color="subtle"
                                pb={4}
                                pt={2}
                                size="sm"
                            >
                                Heeft de patiënt de Module niet ontvangen? Via het blauwe activiteiten symbool (rechts
                                in beeld) in je persoonlijke therapieland omgeving, kun je via openstaande uitnodigingen
                                de module opnieuw versturen. Let op! Wanneer de patiënt deze module al eerder heeft
                                doorlopen kan deze niet nogmaals worden verstuurd. Neem dan contact op met onze
                                servicedesk.
                            </Text>
                        </SupportCollapse>
                        <SupportCollapse header="Hoe kan ik een zorgactiviteit afbreken of annuleren?">
                            <Text
                                color="subtle"
                                pb={4}
                                pt={2}
                                size="sm"
                            >
                                Opgeslagen concepten kun je zelf annuleren. Klik op &apos;bewerk onderzoek&apos; en klik
                                op &apos;annuleren&apos;. Voor het afbreken van een zorg activiteit dien je contact op
                                te nemen met onze servicedesk.
                            </Text>
                        </SupportCollapse>
                    </Box>
                    <Link
                        color="secondary"
                        href="https://www.ksyos.nl/faq/"
                        iconRight="caret-right"
                        openInNewTab
                        sx={{ mb: [4, 6, 8, 10, 12] }}
                        variant="externalLink"
                    >
                        Alle veelgestelde vragen
                    </Link>
                    <Heading
                        mb={[4, 6]}
                        size="large"
                    >
                        Neem contact op
                    </Heading>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={4}
                        justifyContent="space-around"
                    >
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                        >
                            <img
                                alt="contact-icon"
                                height={115}
                                src="/assets/images/contact-icon.svg"
                                width={138}
                            ></img>
                            <Link
                                color="secondary"
                                href="https://www.ksyos.nl/contact/"
                                openInNewTab
                                sx={{ mt: 2 }}
                                variant="externalLink"
                            >
                                Contactformulier
                            </Link>
                            <Text
                                mt={1}
                                size="sm"
                            >
                                De snelste manier
                            </Text>
                        </Box>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                        >
                            <img
                                alt="email-icon"
                                height={115}
                                src="/assets/images/email-icon.svg"
                                width={138}
                            ></img>
                            <Link
                                color="secondary"
                                href="mailto:info@ksyos.nl"
                                openInNewTab
                                sx={{ mt: 2 }}
                                variant="externalLink"
                            >
                                E-mail
                            </Link>
                            <Text
                                mt={1}
                                size="sm"
                            >
                                info@ksyos.nl
                            </Text>
                        </Box>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                        >
                            <img
                                alt="service-desk-icon"
                                height={115}
                                src="/assets/images/service-desk-icon.svg"
                                width={138}
                            ></img>
                            <Text
                                color="secondary"
                                fontWeight="bold"
                                mt={2}
                                size="sm"
                            >
                                Servicedesk
                            </Text>
                            <Text
                                mt={1}
                                size="sm"
                            >
                                +31 (0) 20 600 00 60
                            </Text>
                            <Text size="sm">(8.30 - 17.00)</Text>
                        </Box>
                    </Box>
                </Drawer.Body>
                <Box
                    backgroundColor="neutral500"
                    display="flex"
                    height="48px"
                    px={[6, 8]}
                >
                    <Link
                        href="https://ksyos.nl/privacy-informatiebeveiliging/"
                        openInNewTab
                        sx={{ '&:hover, &:focus': { color: 'white' }, color: 'white', marginRight: [4, 8] }}
                        variant="externalLink"
                    >
                        Privacy
                    </Link>
                    <Link
                        href="https://www.ksyos.nl/veiligwerken/"
                        openInNewTab
                        sx={{ '&:hover, &:focus': { color: 'white' }, color: 'white' }}
                        variant="externalLink"
                    >
                        Veilig werken (NEN 7510)
                    </Link>
                </Box>
            </Drawer>
        </>
    );
    function renderAccountCreationOrChangeInfoByOrganizationType(): JSX.Element {
        return belongsToAOrganizationTypeWithRightsToCreateAccountsAndEndEmployments ? (
            <>
                Via de knop &quot;Zorgverlener toevoegen&quot; op de pagina van jouw&nbsp;
                <Link
                    color="secondary"
                    onClick={toggle}
                    to={`/organizations/${organizationId}/view`}
                    variant="link"
                >
                    organisatie
                </Link>
                &nbsp;kun je een account voor je collega aanvragen.
                <br />
                <br /> Je kunt je eigen account wijzigen via de pagina&apos;s &nbsp;
                <Link
                    color="secondary"
                    onClick={toggle}
                    to={`/practitioners/${practitionerId}/profile`}
                    variant="link"
                >
                    profiel
                </Link>
                &nbsp;en&nbsp;
                <Link
                    color="secondary"
                    onClick={toggle}
                    to={`/employments/${employmentId}/personalSettings`}
                    variant="link"
                >
                    persoonlijke instellingen
                </Link>
                .
            </>
        ) : (
            <>
                Voor het aanmaken van een nieuw account of wijzigen van een bestaand account kun je&nbsp;
                <Link
                    color="secondary"
                    href="https://www.ksyos.nl/aanvragen-wijzigen-account/"
                    openInNewTab
                    variant="externalLink"
                >
                    dit formulier
                </Link>
                &nbsp;invullen.
            </>
        );
    }
};
